// IndexedDBを使用した顔データと打刻データのローカルストレージ

const DB_NAME = 'prc-time-db';
const DB_VERSION = 1;

const STORES = {
  FACE_DATA: 'face-data',
  PENDING_ATTENDANCE: 'pending-attendance',
  SETTINGS: 'settings'
};

let db = null;

// データベースを開く
export const openDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // 顔データストア
      if (!database.objectStoreNames.contains(STORES.FACE_DATA)) {
        const faceStore = database.createObjectStore(STORES.FACE_DATA, { keyPath: 'id' });
        faceStore.createIndex('companyCode', 'companyCode', { unique: false });
        faceStore.createIndex('employeeId', 'employeeId', { unique: false });
      }

      // 未送信の打刻データストア
      if (!database.objectStoreNames.contains(STORES.PENDING_ATTENDANCE)) {
        const attendanceStore = database.createObjectStore(STORES.PENDING_ATTENDANCE, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        attendanceStore.createIndex('employeeId', 'employeeId', { unique: false });
        attendanceStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // 設定ストア
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
};

// 顔データを保存
export const saveFaceData = async (companyCode, employees) => {
  const database = await openDB();
  const tx = database.transaction(STORES.FACE_DATA, 'readwrite');
  const store = tx.objectStore(STORES.FACE_DATA);

  // 既存のデータを削除
  const existingData = await getAllByIndex(store, 'companyCode', companyCode);
  for (const item of existingData) {
    store.delete(item.id);
  }

  // 新しいデータを保存
  for (const emp of employees) {
    for (const face of (emp.face_encodings || [])) {
      if (face.descriptor) {
        store.put({
          id: `${emp.id}-${face.id}`,
          companyCode,
          employeeId: emp.id,
          employeeName: emp.name,
          faceId: face.id,
          descriptor: face.descriptor,
          updatedAt: Date.now()
        });
      }
    }
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

// 顔データを取得
export const getFaceData = async (companyCode) => {
  const database = await openDB();
  const tx = database.transaction(STORES.FACE_DATA, 'readonly');
  const store = tx.objectStore(STORES.FACE_DATA);
  const index = store.index('companyCode');

  return new Promise((resolve, reject) => {
    const request = index.getAll(companyCode);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// 打刻データを保存（オフライン用）
export const savePendingAttendance = async (data) => {
  const database = await openDB();
  const tx = database.transaction(STORES.PENDING_ATTENDANCE, 'readwrite');
  const store = tx.objectStore(STORES.PENDING_ATTENDANCE);

  return new Promise((resolve, reject) => {
    const request = store.add({
      ...data,
      timestamp: Date.now(),
      synced: false
    });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// 未同期の打刻データを取得
export const getPendingAttendance = async () => {
  const database = await openDB();
  const tx = database.transaction(STORES.PENDING_ATTENDANCE, 'readonly');
  const store = tx.objectStore(STORES.PENDING_ATTENDANCE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result.filter(item => !item.synced));
    request.onerror = () => reject(request.error);
  });
};

// 打刻データを同期済みにマーク
export const markAttendanceSynced = async (id) => {
  const database = await openDB();
  const tx = database.transaction(STORES.PENDING_ATTENDANCE, 'readwrite');
  const store = tx.objectStore(STORES.PENDING_ATTENDANCE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const data = getRequest.result;
      if (data) {
        data.synced = true;
        const putRequest = store.put(data);
        putRequest.onsuccess = () => resolve(true);
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve(false);
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

// 同期済みの打刻データを削除
export const clearSyncedAttendance = async () => {
  const database = await openDB();
  const tx = database.transaction(STORES.PENDING_ATTENDANCE, 'readwrite');
  const store = tx.objectStore(STORES.PENDING_ATTENDANCE);

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const synced = request.result.filter(item => item.synced);
      for (const item of synced) {
        store.delete(item.id);
      }
      resolve(synced.length);
    };
    request.onerror = () => reject(request.error);
  });
};

// 設定を保存
export const saveSetting = async (key, value) => {
  const database = await openDB();
  const tx = database.transaction(STORES.SETTINGS, 'readwrite');
  const store = tx.objectStore(STORES.SETTINGS);

  return new Promise((resolve, reject) => {
    const request = store.put({ key, value, updatedAt: Date.now() });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// 設定を取得
export const getSetting = async (key) => {
  const database = await openDB();
  const tx = database.transaction(STORES.SETTINGS, 'readonly');
  const store = tx.objectStore(STORES.SETTINGS);

  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
};

// ヘルパー関数: インデックスからすべてのデータを取得
const getAllByIndex = (store, indexName, value) => {
  return new Promise((resolve, reject) => {
    const index = store.index(indexName);
    const request = index.getAll(value);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// オンライン状態の監視
export const isOnline = () => navigator.onLine;

export const onOnlineStatusChange = (callback) => {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
  
  return () => {
    window.removeEventListener('online', () => callback(true));
    window.removeEventListener('offline', () => callback(false));
  };
};

// バックグラウンド同期をリクエスト
export const requestBackgroundSync = async (tag = 'sync-attendance') => {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const registration = await navigator.serviceWorker.ready;
    try {
      await registration.sync.register(tag);
      console.log('Background sync registered:', tag);
      return true;
    } catch (err) {
      console.log('Background sync registration failed:', err);
      return false;
    }
  }
  return false;
};

export default {
  openDB,
  saveFaceData,
  getFaceData,
  savePendingAttendance,
  getPendingAttendance,
  markAttendanceSynced,
  clearSyncedAttendance,
  saveSetting,
  getSetting,
  isOnline,
  onOnlineStatusChange,
  requestBackgroundSync
};
